import React from 'react'
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Grid,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { Controller, useFieldArray } from 'react-hook-form'
import Field from './Field'
import PrizeRow, { emptyPrize } from './PrizeRow'
import RowActions from './RowActions'
import { impliedTicketFields } from '../../utils/luckyDraw'

export const emptyGroup = { name: '', ticketFields: [], maxWins: 1, prizes: [emptyPrize] }

const toggleTicketField = (ticketFields = [], key, checked) =>
  checked ? [...ticketFields, key] : ticketFields.filter((field) => field !== key)

/**
 * One prize group: what it draws from, and the prizes inside it. `startNumber` is the running
 * position of this group's first prize across the whole draw.
 *
 * Memoised, so every prop has to be referentially stable: `swapGroup`/`removeGroup` are passed in
 * as useFieldArray's own functions rather than as closures built per render, and the handlers are
 * built here instead. Without that, a keystroke anywhere in the form re-renders every prize row.
 */
const GroupCard = ({
  control,
  getValues,
  index,
  startNumber,
  awarded,
  ticketFieldOptions,
  isFirst,
  isLast,
  canRemove,
  swapGroup,
  removeGroup,
}) => {
  const { fields, append, insert, remove, swap } = useFieldArray({
    control,
    name: `groups.${index}.prizes`,
  })

  const isLocked = awarded > 0

  /**
   * A duplicate lands right after its source. Read through getValues rather than `fields`, which
   * holds a snapshot from the last append/remove rather than what has been typed since. prizeId and
   * awarded are dropped: the copy is a new prize with no winners, and keeping the id would submit
   * two prizes sharing one.
   */
  const clonePrize = (prizeIndex) => {
    const { prizeId, awarded: ignored, ...values } = getValues(
      `groups.${index}.prizes.${prizeIndex}`,
    )

    insert(prizeIndex + 1, values)
  }

  return (
    <>
      <Grid item xs={12} display='flex' justifyContent='space-between' alignItems='center'>
        <Typography variant='h5' sx={{ fontSize: '1.25rem' }}>
          Group {index + 1}
        </Typography>
        <RowActions
          onUp={() => swapGroup(index, index - 1)}
          onDown={() => swapGroup(index, index + 1)}
          isFirst={isFirst}
          isLast={isLast}
          onRemove={() => removeGroup(index)}
          canRemove={!isLocked && canRemove}
          removeTitle={isLocked ? 'Already awarded — cannot remove' : 'Remove group'}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <Field control={control} name={`groups.${index}.name`} label='Group' />
      </Grid>
      <Grid item xs={6} sm={3}>
        <Field
          control={control}
          name={`groups.${index}.maxWins`}
          label='Max Per Dealer'
          type='number'
          helper='From this group.'
        />
      </Grid>

      {/* Hidden when the source leaves nothing to pick — the field is already filled in. */}
      {impliedTicketFields(ticketFieldOptions).length === 0 && (
        <Grid item xs={12}>
          <Controller
            name={`groups.${index}.ticketFields`}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <FormControl component='fieldset' error={!!error} disabled={isLocked}>
                <FormLabel component='legend'>Tickets counted as entries</FormLabel>
                <FormGroup row>
                  {ticketFieldOptions.map((option) => (
                    <FormControlLabel
                      key={option.key}
                      label={option.label}
                      control={
                        <Checkbox
                          checked={field.value?.includes(option.key) ?? false}
                          onChange={(e) =>
                            field.onChange(
                              toggleTicketField(field.value, option.key, e.target.checked),
                            )
                          }
                        />
                      }
                    />
                  ))}
                </FormGroup>
                <FormHelperText>
                  {error?.message ||
                    (isLocked ? 'Locked — this group has already awarded prizes.' : '')}
                </FormHelperText>
              </FormControl>
            )}
          />
        </Grid>
      )}

      <Grid item xs={12}>
        {/* Outside the card below, so the first prize's band still caps it. */}
        <Typography variant='h5'>Prizes</Typography>
        <Typography variant='body2' color='text.secondary'>
          Winners Per Draw is how many entries one press reveals.
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={2} alignItems='flex-start'>
              {fields.map((prizeField, prizeIndex) => (
                <PrizeRow
                  key={prizeField.id}
                  control={control}
                  name={`groups.${index}.prizes.${prizeIndex}`}
                  number={startNumber + prizeIndex}
                  awarded={prizeField.awarded ?? 0}
                  isFirst={prizeIndex === 0}
                  isLast={prizeIndex === fields.length - 1}
                  canRemove={fields.length > 1}
                  onMoveUp={() => swap(prizeIndex, prizeIndex - 1)}
                  onMoveDown={() => swap(prizeIndex, prizeIndex + 1)}
                  onClone={() => clonePrize(prizeIndex)}
                  onRemove={() => remove(prizeIndex)}
                />
              ))}

              <Grid item xs={12} display='flex' justifyContent='flex-end'>
                <Button startIcon={<AddIcon color='success' />} onClick={() => append(emptyPrize)}>
                  Add Prize
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </>
  )
}

export default React.memo(GroupCard)
